import { User } from '@demoops/shared-types';

/**
 * Shape of /api/v1/me as the backend actually returns it (snake_case role
 * fields, flat permission-name strings). The shared UserWithRoles type
 * predates this payload, so the auth module owns its own view of it.
 */
export interface AuthRole {
  id: number;
  name: string;
  display_name: string;
}

/**
 * Operator experience (2026-09-17): `users.experience`, carried on /me and in
 * the cached offline session. 'operator' collapses the app to My job (one
 * page, three actions); 'full' is everyone else.
 */
export type UserExperience = 'full' | 'operator';

export interface AuthUser extends User {
  roles?: AuthRole[];
  permissions?: string[];
  /** Absent on sessions cached before the field shipped — read as 'full'. */
  experience?: UserExperience;
  /** Signed in, given no access yet (access matrix 2026-10-15). */
  access_pending?: boolean;
}

/**
 * Does this person get the operator screen? Only the literal 'operator'
 * does; a missing field (pre-rollout cached session) or no user reads as
 * the full app. That direction is deliberate: the nav is a courtesy and the
 * API's project/equipment scoping is the wall, so a stale session that
 * shows more menu than it should still cannot reach anything with it.
 */
export function isOperatorExperience(user: AuthUser | null | undefined): boolean {
  return user?.experience === 'operator';
}

/**
 * True when the user holds the named backend permission (e.g.
 * 'projects.create'). Fails closed: no user or no permissions array —
 * including sessions cached before /me included permissions — means no.
 */
export function hasPermission(user: AuthUser | null, permission: string): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

/**
 * A permission name is a WRITE permission unless it is one of the handful
 * of read-only verbs. Mirrors the backend's tier ladder shape without
 * needing a permission-by-permission allowlist: 'view*' reads never write,
 * baseline auth.* actions aren't access at all.
 *
 * The verb is the LAST dot-separated segment, not the second (coordinator
 * review, 2026-09-01): a permission like 'safety.medical.view' has three
 * segments — split('.', 2) took 'medical' as the "verb" and misclassified
 * a read as a write.
 */
function isWritePermission(permission: string): boolean {
  if (permission.startsWith('auth.')) {
    return false;
  }
  const verb = permission.split('.').pop();
  return !(verb === 'view' || verb?.startsWith('view_') || verb?.startsWith('view-'));
}

/**
 * True when the user holds no write permission — derived from EFFECTIVE
 * permissions, not a role name. The 'viewer' role has zero live holders
 * (permissions v2), so the old literal-name check (roles.length === 1 &&
 * roles[0].name === 'viewer') was dead: it drove the persistent Read-only
 * badge for nobody. Fails closed with no permissions data (e.g. a
 * pre-rollout cached session) — no data reads as "not view-only", same as
 * hasPermission()'s posture.
 */
export function isViewerOnly(user: AuthUser | null): boolean {
  const permissions = user?.permissions;
  if (!permissions || permissions.length === 0) {
    return false;
  }
  return !permissions.some(isWritePermission);
}
